import React, { useState, useEffect, useRef } from 'react';
import {
    Box, Typography, Paper, Grid, TextField, Button, MenuItem,
    CircularProgress, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Checkbox, FormControl, InputLabel, Select,
    Alert, Chip, Popover, List, ListItem, ListItemText, ListItemIcon,
    Divider
} from '@mui/material';
import {
    Print, PictureAsPdf,
    Settings, Description, Assessment, ExpandMore
} from '@mui/icons-material';
import { useReactToPrint } from 'react-to-print';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { LCTemplateEnglish, LCTemplateBilingual, LCTemplateLandscape, BonafideTemplateSimple, BonafideTemplateDetailed, BonafideTemplateLandscape, Form15ATemplate, NirgamUtaraTemplate } from '../components/certificates';
import { generateCertificatePDF } from '../utils/pdfUtils';

// Certificate tabs matching ePrashasan
const CERT_TABS = [
    {
        key: 'bonafide', label: 'Bonafide', hasPlus: true,
        subItems: [
            { key: 'setting', label: 'Bonafide Setting', icon: <Settings fontSize="small" /> },
            { key: 'print', label: 'Print Bonafide', icon: <Print fontSize="small" /> },
            { key: 'report', label: 'Bonafide Report', icon: <Assessment fontSize="small" /> },
        ]
    },
    {
        key: 'leaving', label: 'Leaving Certificate', hasPlus: true,
        subItems: [
            { key: 'setting', label: 'LC Setting', icon: <Settings fontSize="small" /> },
            { key: 'print', label: 'Print LC', icon: <Print fontSize="small" /> },
            { key: 'report', label: 'LC Report', icon: <Assessment fontSize="small" /> },
        ]
    },
    { key: 'character', label: 'Character Certificate', subItems: [] },
    { key: 'form15a', label: 'Form No 15A', subItems: [] },
    {
        key: 'nirgam', label: 'Pravesh Nirgam Utara', hasPlus: true,
        subItems: [
            { key: 'print', label: 'Print Nirgam Utara', icon: <Print fontSize="small" /> },
        ]
    },
    { key: 'attendance', label: 'Attendance Certificate', subItems: [] },
];

// Bonafide print format formats
const BONAFIDE_FORMATS = [
    { key: 'preview_a4', label: 'Preview A4 Bonafide', color: '#1565c0' },
    { key: 'preview_a5', label: 'Preview A5 Bonafide', color: '#1565c0' },
    { key: 'college', label: 'Bonafide For College', color: '#1565c0' },
    { key: 'dynamic', label: 'Dynamic Bonafide', color: '#1565c0' },
    { key: 'landscape', label: 'A4 Landscape', color: '#1565c0' },
    { key: 'format6', label: 'Format 6', color: '#1565c0' },
    { key: 'format7', label: 'Format 7', color: '#1565c0' },
    { key: 'format8', label: 'Format 8', color: '#1565c0' },
];

const LC_FORMATS = [
    { key: 'english', label: 'English Header', color: '#1565c0' },
    { key: 'bilingual', label: 'Bilingual (Marathi + English)', color: '#1565c0' },
    { key: 'landscape', label: 'Landscape', color: '#1565c0' },
    { key: 'type3', label: 'Type 3', color: '#1565c0' },
    { key: 'type4', label: 'Type 4', color: '#1565c0' },
    { key: 'senior_tc', label: 'Senior TC', color: '#1565c0' },
    { key: 'jr_college', label: 'Jr. College TC', color: '#1565c0' },
];

const Certificates = () => {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('bonafide');
    const [activeSubTab, setActiveSubTab] = useState('print');
    const [dropdownAnchor, setDropdownAnchor] = useState(null);
    const [dropdownTab, setDropdownTab] = useState(null);

    // Data states
    const [classes, setClasses] = useState([]);
    const [sections, setSections] = useState([]);
    const [students, setStudents] = useState([]);
    const [academicYears, setAcademicYears] = useState([]);
    const [selectedFormat, setSelectedFormat] = useState('preview_a4');
    const [formData, setFormData] = useState({
        academicYearId: '', classId: '', sectionId: '', studentId: '', regNo: '', searchName: ''
    });
    const [certificateData, setCertificateData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [showEditPanel, setShowEditPanel] = useState(false);

    // Real-time edit helper: update nested field in certificateData
    const updateCertField = (section, field, value) => {
        setCertificateData(prev => ({
            ...prev,
            [section]: {
                ...prev[section],
                [field]: value,
            }
        }));
    };

    // Approval states
    const [issuedCertificates, setIssuedCertificates] = useState([]);

    const componentRef = useRef();
    const handlePrint = useReactToPrint({ contentRef: componentRef });

    useEffect(() => { fetchClasses(); fetchAcademicYears(); }, []);

    const fetchClasses = async () => {
        try {
            const res = await api.get('/classes');
            setClasses(res.data.data || []);
        } catch (e) { console.error("Failed to fetch classes"); }
    };

    const fetchAcademicYears = async () => {
        try {
            const res = await api.get('/academic-years');
            setAcademicYears(res.data.data || []);
            if (res.data.data?.length > 0) {
                const current = res.data.data.find(y => y.isCurrent) || res.data.data[0];
                setFormData(prev => ({ ...prev, academicYearId: current.id }));
            }
        } catch (e) { console.error("Failed to fetch academic years"); }
    };

    const handleClassChange = async (e) => {
        const classId = e.target.value;
        setFormData({ ...formData, classId, sectionId: '', studentId: '' });
        setStudents([]);
        try {
            const res = await api.get(`/classes/${classId}/sections`);
            setSections(res.data.data || []);
        } catch (e) { console.error("Failed to fetch sections"); }
    };

    const handleSectionChange = async (e) => {
        const sectionId = e.target.value;
        setFormData({ ...formData, sectionId, studentId: '' });
        try {
            const res = await api.get('/students', {
                params: { class: formData.classId, section: sectionId, status: 'active' }
            });
            setStudents(res.data.data || []);
        } catch (e) { console.error("Failed to fetch students"); }
    };

    const getCertType = () => {
        const map = {
            bonafide: 'bonafide',
            leaving: 'leaving_certificate',
            character: 'character',
            attendance: 'attendance',
            form15a: 'bonafide',
            nirgam: 'leaving_certificate'
        };
        return map[activeTab] || 'bonafide';
    };

    const handleSearch = async () => {
        if (!formData.studentId) { toast.warning("Please select a student"); return; }
        setLoading(true);
        try {
            const res = await api.post('/certificates/generate-preview', {
                studentId: formData.studentId, type: getCertType(), subType: selectedFormat
            });
            setCertificateData(res.data.data);
            toast.success("Certificate data loaded");
        } catch (e) { toast.error("Failed to generate certificate preview"); }
        finally { setLoading(false); }
    };

    const handleCreateCertificate = async () => {
        if (!formData.studentId) { toast.warning("Please select a student"); return; }
        setLoading(true);
        try {
            await api.post('/certificates', {
                studentId: formData.studentId, type: getCertType(),
                subType: selectedFormat, academicYearId: formData.academicYearId
            });
            toast.success("Certificate request created successfully");
        } catch (e) { toast.error("Failed to create certificate request"); }
        finally { setLoading(false); }
    };

    const handleTabClick = (tab, event) => {
        if (tab.subItems && tab.subItems.length > 0) {
            setDropdownAnchor(event.currentTarget);
            setDropdownTab(tab);
        } else {
            setActiveTab(tab.key);
            setActiveSubTab('print');
            setCertificateData(null);
        }
    };

    const handleSubItemClick = (tab, subItem) => {
        setDropdownAnchor(null);
        setDropdownTab(null);
        setActiveTab(tab.key);
        setCertificateData(null);

        if (subItem.key === 'setting') {
            if (tab.key === 'bonafide') {
                navigate('/certificates/settings');
                return;
            }
            setActiveSubTab('setting');
        } else if (subItem.key === 'report') {
            setActiveSubTab('report');
        } else {
            setActiveSubTab('print');
        }
    };

    const renderCertificateTemplate = () => {
        if (!certificateData) return null;
        if (activeTab === 'leaving') {
            if (selectedFormat === 'bilingual') return <LCTemplateBilingual data={certificateData} />;
            if (selectedFormat === 'landscape') return <LCTemplateLandscape data={certificateData} />;
            return <LCTemplateEnglish data={certificateData} />;
        }
        if (activeTab === 'bonafide') {
            if (selectedFormat === 'preview_a5' || selectedFormat === 'college')
                return <BonafideTemplateSimple data={certificateData} />;
            if (selectedFormat === 'landscape')
                return <BonafideTemplateLandscape data={certificateData} />;
            return <BonafideTemplateDetailed data={certificateData} />;
        }
        if (activeTab === 'form15a') {
            return <Form15ATemplate data={certificateData} />;
        }
        if (activeTab === 'nirgam') {
            return <NirgamUtaraTemplate data={certificateData} />;
        }
        // Generic template for character/attendance
        const { school, student, details, issueDate } = certificateData;
        return (
            <Box sx={{ fontFamily: 'serif', fontSize: 14, lineHeight: 1.8 }}>
                <Box sx={{ textAlign: 'center', mb: 3 }}>
                    <Typography variant="h5" sx={{ fontWeight: 'bold' }}>{school?.name}</Typography>
                    <Typography>{school?.address}</Typography>
                </Box>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h5" sx={{ textAlign: 'center', mb: 3, textDecoration: 'underline' }}>
                    {activeTab === 'character' ? 'CHARACTER CERTIFICATE' : 'ATTENDANCE CERTIFICATE'}
                </Typography>
                {activeTab === 'character' && (
                    <Typography sx={{ mb: 3 }}>
                        This is to certify that <strong>{student?.name}</strong> is a student of this school.
                        His/Her character and conduct are <strong>{details?.character || 'Good'}</strong>.
                    </Typography>
                )}
                {activeTab === 'attendance' && (
                    <Typography sx={{ mb: 3 }}>
                        This is to certify that <strong>{student?.name}</strong> was in full-time attendance
                        at {school?.name}. His/Her attendance is more than <strong>{details?.attendancePercentage}%</strong>.
                    </Typography>
                )}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 8 }}>
                    <Box sx={{ textAlign: 'center' }}>
                        <Box sx={{ borderTop: '1px solid black', width: 150, mb: 1 }} />
                        <Typography>Clerk</Typography>
                    </Box>
                    <Typography>Date: {issueDate}</Typography>
                    <Box sx={{ textAlign: 'center' }}>
                        <Box sx={{ borderTop: '1px solid black', width: 150, mb: 1 }} />
                        <Typography>Head Master</Typography>
                    </Box>
                </Box>
            </Box>
        );
    };

    const getFormatButtons = () => {
        if (activeTab === 'bonafide') return BONAFIDE_FORMATS;
        if (activeTab === 'leaving') return LC_FORMATS;
        return [];
    };

    return (
        <Box sx={{ p: 2 }}>
            {/* Page Title */}
            <Typography variant="h5" fontWeight={700} sx={{ mb: 2 }}>Certificates</Typography>

            {/* ePrashasan-style Tab Bar */}
            <Paper sx={{
                display: 'flex', flexWrap: 'wrap', gap: 0, mb: 3,
                borderRadius: 1, overflow: 'hidden', bgcolor: '#fff',
                border: '1px solid #e0e0e0'
            }}>
                {CERT_TABS.map((tab) => (
                    <Box
                        key={tab.key}
                        onClick={(e) => handleTabClick(tab, e)}
                        sx={{
                            display: 'flex', alignItems: 'center', gap: 0.5,
                            px: 2, py: 1.2, cursor: 'pointer',
                            borderBottom: activeTab === tab.key ? '3px solid #1a9e8f' : '3px solid transparent',
                            bgcolor: activeTab === tab.key ? '#f0faf9' : 'transparent',
                            transition: 'all 0.2s',
                            '&:hover': { bgcolor: '#f5f5f5' },
                            fontSize: 13, fontWeight: activeTab === tab.key ? 700 : 500,
                            color: activeTab === tab.key ? '#1a9e8f' : '#555',
                            userSelect: 'none',
                        }}
                    >
                        <Description sx={{ fontSize: 16, opacity: 0.7 }} />
                        <span>{tab.label}</span>
                        {tab.hasPlus && <ExpandMore sx={{ fontSize: 16, ml: 0.3 }} />}
                    </Box>
                ))}
            </Paper>

            {/* Dropdown Popover */}
            <Popover
                open={Boolean(dropdownAnchor)}
                anchorEl={dropdownAnchor}
                onClose={() => { setDropdownAnchor(null); setDropdownTab(null); }}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                PaperProps={{ sx: { borderRadius: 1, boxShadow: '0 4px 20px rgba(0,0,0,0.15)', minWidth: 200 } }}
            >
                {dropdownTab && (
                    <List disablePadding>
                        {dropdownTab.subItems.map((sub, idx) => (
                            <ListItem
                                key={sub.key}
                                button
                                onClick={() => handleSubItemClick(dropdownTab, sub)}
                                sx={{
                                    py: 1, px: 2,
                                    borderBottom: idx < dropdownTab.subItems.length - 1 ? '1px solid #f0f0f0' : 'none',
                                    '&:hover': { bgcolor: '#f0faf9' }
                                }}
                            >
                                <ListItemIcon sx={{ minWidth: 32 }}>{sub.icon}</ListItemIcon>
                                <ListItemText primary={sub.label} primaryTypographyProps={{ fontSize: 13 }} />
                            </ListItem>
                        ))}
                    </List>
                )}
            </Popover>

            {/* Print Sub-Tab Content */}
            {activeSubTab === 'print' && (
                <>
                    {/* Format Selection Buttons */}
                    {getFormatButtons().length > 0 && (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                            {getFormatButtons().map(fmt => (
                                <Button
                                    key={fmt.key}
                                    variant={selectedFormat === fmt.key ? 'contained' : 'outlined'}
                                    size="small"
                                    onClick={() => setSelectedFormat(fmt.key)}
                                    sx={{
                                        fontSize: 11, py: 0.5, px: 1.5, textTransform: 'none',
                                        bgcolor: selectedFormat === fmt.key ? '#1565c0' : 'transparent',
                                        borderColor: '#1565c0', color: selectedFormat === fmt.key ? '#fff' : '#1565c0',
                                        '&:hover': { bgcolor: selectedFormat === fmt.key ? '#0d47a1' : '#e3f2fd' }
                                    }}
                                >
                                    {fmt.label}
                                </Button>
                            ))}
                        </Box>
                    )}

                    {/* Search Form */}
                    <Paper sx={{ p: 2, mb: 3 }}>
                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} md={2}>
                                <FormControl fullWidth size="small">
                                    <InputLabel>Academic Year</InputLabel>
                                    <Select value={formData.academicYearId} label="Academic Year"
                                        onChange={e => setFormData({ ...formData, academicYearId: e.target.value })}>
                                        {academicYears.map(y => <MenuItem key={y.id} value={y.id}>{y.name}</MenuItem>)}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} md={2}>
                                <FormControl fullWidth size="small">
                                    <InputLabel>Class</InputLabel>
                                    <Select value={formData.classId} label="Class" onChange={handleClassChange}>
                                        {classes.map(c => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} md={2}>
                                <FormControl fullWidth size="small">
                                    <InputLabel>Division</InputLabel>
                                    <Select value={formData.sectionId} label="Division"
                                        onChange={handleSectionChange} disabled={!formData.classId}>
                                        {sections.map(s => <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>)}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} md={2}>
                                <TextField fullWidth size="small" label="Reg No" value={formData.regNo}
                                    onChange={e => setFormData({ ...formData, regNo: e.target.value })} />
                            </Grid>
                            <Grid item xs={12} md={2}>
                                <TextField fullWidth size="small" label="Search by Name" value={formData.searchName}
                                    onChange={e => setFormData({ ...formData, searchName: e.target.value })} />
                            </Grid>
                            <Grid item xs={12} md={2}>
                                <Button variant="contained" fullWidth onClick={handleSearch} disabled={loading}
                                    sx={{ bgcolor: '#1a9e8f', '&:hover': { bgcolor: '#158a7d' } }}>
                                    {loading ? <CircularProgress size={20} /> : 'Search'}
                                </Button>
                            </Grid>
                        </Grid>

                        {/* Student List */}
                        {students.length > 0 && (
                            <Box sx={{ mt: 2 }}>
                                <Typography variant="subtitle2" gutterBottom>Select Student:</Typography>
                                <TableContainer sx={{ maxHeight: 250 }}>
                                    <Table size="small" stickyHeader>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell padding="checkbox" />
                                                <TableCell>Reg No</TableCell>
                                                <TableCell>Name</TableCell>
                                                <TableCell>Class</TableCell>
                                                <TableCell>Gender</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {students.map(std => (
                                                <TableRow key={std.id} hover
                                                    selected={formData.studentId === std.id}
                                                    onClick={() => setFormData({ ...formData, studentId: std.id })}
                                                    sx={{ cursor: 'pointer' }}>
                                                    <TableCell padding="checkbox">
                                                        <Checkbox checked={formData.studentId === std.id} size="small" />
                                                    </TableCell>
                                                    <TableCell>{std.admissionNumber}</TableCell>
                                                    <TableCell>{std.user?.lastName} {std.user?.firstName}</TableCell>
                                                    <TableCell>{std.class?.name}</TableCell>
                                                    <TableCell>{std.user?.gender}</TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Box>
                        )}
                    </Paper>

                    {/* Certificate Preview with Real-Time Edit */}
                    {certificateData && (
                        <Paper sx={{ p: 0, overflow: 'hidden' }}>
                            {/* Action buttons bar */}
                            <Box sx={{
                                display: 'flex', flexWrap: 'wrap', gap: 1, p: 2,
                                bgcolor: '#f8f9fa', borderBottom: '1px solid #e0e0e0',
                                alignItems: 'center',
                            }}>
                                <Button variant="contained" size="small" startIcon={<Print />} onClick={handlePrint}
                                    sx={{ bgcolor: '#2196f3' }}>
                                    Print A4
                                </Button>
                                <Button variant="contained" size="small" color="success">
                                    Cr Photo
                                </Button>
                                <Button variant="contained" size="small" color="error"
                                    onClick={() => setCertificateData(null)}>
                                    Back
                                </Button>
                                <Button variant="outlined" size="small" startIcon={<PictureAsPdf />}
                                    onClick={async () => {
                                        try {
                                            if (componentRef.current) {
                                                await generateCertificatePDF(componentRef.current, activeTab, certificateData?.student?.name || 'student');
                                                toast.success('PDF downloaded');
                                            }
                                        } catch (e) { toast.error('Failed to generate PDF'); }
                                    }}>
                                    Download PDF
                                </Button>
                                <Button variant="outlined" size="small" onClick={handleCreateCertificate}>
                                    Save & Create Request
                                </Button>
                                <Box sx={{ flexGrow: 1 }} />
                                <Button
                                    variant={showEditPanel ? 'contained' : 'outlined'}
                                    size="small"
                                    startIcon={<Settings />}
                                    onClick={() => setShowEditPanel(!showEditPanel)}
                                    sx={{
                                        bgcolor: showEditPanel ? '#ff9800' : 'transparent',
                                        borderColor: '#ff9800', color: showEditPanel ? '#fff' : '#ff9800',
                                        '&:hover': { bgcolor: showEditPanel ? '#f57c00' : '#fff3e0' }
                                    }}
                                >
                                    {showEditPanel ? 'Hide Editor' : '✏️ Edit Fields'}
                                </Button>
                            </Box>

                            {/* Content: Editor Panel + Certificate */}
                            <Box sx={{ display: 'flex' }}>
                                {/* Editable Fields Panel */}
                                {showEditPanel && (
                                    <Box sx={{
                                        width: 340, minWidth: 340, borderRight: '1px solid #e0e0e0',
                                        bgcolor: '#fafafa', overflowY: 'auto', maxHeight: '80vh',
                                        p: 2,
                                    }}>
                                        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5, color: '#1a9e8f' }}>
                                            ✏️ Edit Certificate Fields
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                                            Changes update the preview in real-time
                                        </Typography>

                                        {/* Student Info */}
                                        <Typography variant="caption" fontWeight={700} sx={{ mb: 0.5, display: 'block', color: '#555', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                            Student Details
                                        </Typography>
                                        <EditField label="Student Name" value={certificateData.student?.name}
                                            onChange={(v) => updateCertField('student', 'name', v)} />
                                        <EditField label="Father Name" value={certificateData.student?.fatherName}
                                            onChange={(v) => updateCertField('student', 'fatherName', v)} />
                                        <EditField label="Mother Name" value={certificateData.student?.motherName}
                                            onChange={(v) => updateCertField('student', 'motherName', v)} />
                                        <EditField label="Admission No" value={certificateData.student?.admissionNumber}
                                            onChange={(v) => updateCertField('student', 'admissionNumber', v)} />

                                        <Divider sx={{ my: 1.5 }} />
                                        <Typography variant="caption" fontWeight={700} sx={{ mb: 0.5, display: 'block', color: '#555', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                            Certificate Details
                                        </Typography>
                                        <EditField label="Nationality" value={certificateData.details?.nationality}
                                            onChange={(v) => updateCertField('details', 'nationality', v)} />
                                        <EditField label="Mother Tongue" value={certificateData.details?.motherTongue}
                                            onChange={(v) => updateCertField('details', 'motherTongue', v)} />
                                        <EditField label="Religion" value={certificateData.details?.religion}
                                            onChange={(v) => updateCertField('details', 'religion', v)} />
                                        <EditField label="Caste" value={certificateData.details?.caste}
                                            onChange={(v) => updateCertField('details', 'caste', v)} />
                                        <EditField label="Sub Caste" value={certificateData.details?.subCaste}
                                            onChange={(v) => updateCertField('details', 'subCaste', v)} />
                                        <EditField label="Place of Birth" value={certificateData.details?.placeOfBirth}
                                            onChange={(v) => updateCertField('details', 'placeOfBirth', v)} />
                                        <EditField label="Taluka" value={certificateData.details?.taluka}
                                            onChange={(v) => updateCertField('details', 'taluka', v)} />
                                        <EditField label="District" value={certificateData.details?.district}
                                            onChange={(v) => updateCertField('details', 'district', v)} />
                                        <EditField label="Date of Birth" value={certificateData.details?.dateOfBirth} type="date"
                                            onChange={(v) => updateCertField('details', 'dateOfBirth', v)} />
                                        <EditField label="DOB in Words" value={certificateData.details?.dateOfBirthInWords}
                                            onChange={(v) => updateCertField('details', 'dateOfBirthInWords', v)} />
                                        <EditField label="UID (Aadhar)" value={certificateData.details?.uid}
                                            onChange={(v) => updateCertField('details', 'uid', v)} />

                                        <Divider sx={{ my: 1.5 }} />
                                        <Typography variant="caption" fontWeight={700} sx={{ mb: 0.5, display: 'block', color: '#555', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                            School & Admission
                                        </Typography>
                                        <EditField label="Last School" value={certificateData.details?.lastSchool}
                                            onChange={(v) => updateCertField('details', 'lastSchool', v)} />
                                        <EditField label="Date of Admission" value={certificateData.details?.dateOfAdmission} type="date"
                                            onChange={(v) => updateCertField('details', 'dateOfAdmission', v)} />
                                        <EditField label="Class at Admission" value={certificateData.details?.classAtAdmission}
                                            onChange={(v) => updateCertField('details', 'classAtAdmission', v)} />
                                        <EditField label="Current Class" value={certificateData.details?.currentClass}
                                            onChange={(v) => updateCertField('details', 'currentClass', v)} />
                                        <EditField label="Studying Since" value={certificateData.details?.studyingSince}
                                            onChange={(v) => updateCertField('details', 'studyingSince', v)} />

                                        <Divider sx={{ my: 1.5 }} />
                                        <Typography variant="caption" fontWeight={700} sx={{ mb: 0.5, display: 'block', color: '#555', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                            Leaving Details
                                        </Typography>
                                        <EditField label="Progress" value={certificateData.details?.progress}
                                            onChange={(v) => updateCertField('details', 'progress', v)} />
                                        <EditField label="Conduct" value={certificateData.details?.conduct}
                                            onChange={(v) => updateCertField('details', 'conduct', v)} />
                                        <EditField label="Date of Leaving" value={certificateData.details?.dateOfLeaving} type="date"
                                            onChange={(v) => updateCertField('details', 'dateOfLeaving', v)} />
                                        <EditField label="Reason for Leaving" value={certificateData.details?.reasonForLeaving}
                                            onChange={(v) => updateCertField('details', 'reasonForLeaving', v)} />
                                        <EditField label="Remarks" value={certificateData.details?.remarks}
                                            onChange={(v) => updateCertField('details', 'remarks', v)} />

                                        <Divider sx={{ my: 1.5 }} />
                                        <Typography variant="caption" fontWeight={700} sx={{ mb: 0.5, display: 'block', color: '#555', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                            Register Numbers
                                        </Typography>
                                        <EditField label="Sr. Number" value={certificateData.details?.srNumber}
                                            onChange={(v) => updateCertField('details', 'srNumber', v)} />
                                        <EditField label="Index Number" value={certificateData.details?.indexNumber}
                                            onChange={(v) => updateCertField('details', 'indexNumber', v)} />
                                        <EditField label="General Reg. No" value={certificateData.details?.generalRegisterNo}
                                            onChange={(v) => updateCertField('details', 'generalRegisterNo', v)} />
                                        <EditField label="Issue Date" value={certificateData.issueDate} type="date"
                                            onChange={(v) => setCertificateData(prev => ({ ...prev, issueDate: v }))} />
                                    </Box>
                                )}

                                {/* Certificate content */}
                                <Box sx={{ flex: 1, p: 3, bgcolor: '#f5f5f5', overflowY: 'auto', maxHeight: '80vh' }}>
                                    <Box ref={componentRef} sx={{
                                        background: 'white', border: '1px solid #ccc',
                                        maxWidth: 900, mx: 'auto', p: 2
                                    }}>
                                        {renderCertificateTemplate()}
                                    </Box>
                                </Box>
                            </Box>
                        </Paper>
                    )}
                </>
            )}

            {/* Report Sub-Tab */}
            {activeSubTab === 'report' && (
                <Paper sx={{ p: 3 }}>
                    <Typography variant="h6" gutterBottom>
                        {activeTab === 'bonafide' ? 'Bonafide' : 'LC'} Report
                    </Typography>
                    <Alert severity="info">
                        Select filters and click Search to view issued certificates.
                    </Alert>
                    {/* Report filters and table would go here */}
                    <Box sx={{ mt: 2 }}>
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={3}>
                                <FormControl fullWidth size="small">
                                    <InputLabel>Academic Year</InputLabel>
                                    <Select value={formData.academicYearId} label="Academic Year"
                                        onChange={e => setFormData({ ...formData, academicYearId: e.target.value })}>
                                        {academicYears.map(y => <MenuItem key={y.id} value={y.id}>{y.name}</MenuItem>)}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <FormControl fullWidth size="small">
                                    <InputLabel>Class</InputLabel>
                                    <Select value={formData.classId} label="Class" onChange={handleClassChange}>
                                        {classes.map(c => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <Button variant="contained" onClick={async () => {
                                    try {
                                        const res = await api.get('/certificates', { params: { type: getCertType(), status: 'issued' } });
                                        setIssuedCertificates(res.data.data || []);
                                    } catch (e) { toast.error('Failed to fetch certificates'); }
                                }} sx={{ bgcolor: '#1a9e8f', mt: 0.5 }}>
                                    Search
                                </Button>
                            </Grid>
                        </Grid>
                        {issuedCertificates.length > 0 && (
                            <TableContainer sx={{ mt: 2 }}>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                                            <TableCell sx={{ fontWeight: 700 }}>Sr No</TableCell>
                                            <TableCell sx={{ fontWeight: 700 }}>Serial No</TableCell>
                                            <TableCell sx={{ fontWeight: 700 }}>Student Name</TableCell>
                                            <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                                            <TableCell sx={{ fontWeight: 700 }}>Issue Date</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {issuedCertificates.map((cert, idx) => (
                                            <TableRow key={cert.id} hover>
                                                <TableCell>{idx + 1}</TableCell>
                                                <TableCell>{cert.serialNumber}</TableCell>
                                                <TableCell>{cert.student?.user?.firstName} {cert.student?.user?.lastName}</TableCell>
                                                <TableCell>
                                                    <Chip label={cert.status} size="small"
                                                        color={cert.status === 'issued' ? 'success' : 'warning'} />
                                                </TableCell>
                                                <TableCell>{cert.issueDate ? new Date(cert.issueDate).toLocaleDateString() : '-'}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </Box>
                </Paper>
            )}

            {/* Setting Sub-Tab (for LC) */}
            {activeSubTab === 'setting' && (
                <Paper sx={{ p: 3 }}>
                    <Typography variant="h6" gutterBottom>
                        {activeTab === 'leaving' ? 'Leaving Certificate' : activeTab} Settings
                    </Typography>
                    <Alert severity="info">
                        Configure the fields and format for this certificate type.
                    </Alert>
                </Paper>
            )}
        </Box>
    );
};

// Compact inline edit field component for the certificate editor panel
const EditField = ({ label, value, onChange, type = 'text' }) => (
    <TextField
        fullWidth size="small" label={label}
        type={type}
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        InputLabelProps={type === 'date' ? { shrink: true } : undefined}
        sx={{
            mb: 1,
            '& .MuiOutlinedInput-root': {
                borderRadius: '6px', fontSize: 12,
                '& input': { py: 0.8, px: 1 },
            },
            '& .MuiInputLabel-root': { fontSize: 11 },
        }}
    />
);

export default Certificates;
